import { Form, Formik } from 'formik';

import FormField from '../FormField';
import { IField } from '../interfaces/IField';
import { shopifySubscriptionDiscountSchema } from './schema/shopify-subscription-discount.schema';

import Button from '@/components/ui/button';
import { SubscriptionPlans } from '@/components/utils/enums';
import { ICreateShopifySubscriptionDiscountPayload } from '@/interfaces/api/shopify-subscription-discount/ICreateShopifySubscriptionDiscountPayload';

const values: ICreateShopifySubscriptionDiscountPayload = {
	email: '',
	planName: '',
	percentage: 0,
	durationMonths: 1,
};

export enum ShopifySubscriptionDiscountOptions {
	VIEW = 'VIEW',
	CREATE = 'CREATE',
	UPDATE = 'UPDATE',
}

type PropTypes = {
	handleSubmit: (values: ICreateShopifySubscriptionDiscountPayload) => void;
	loading: boolean;
	shopifySubscriptionDiscountOption: ShopifySubscriptionDiscountOptions;
	initialValues?: ICreateShopifySubscriptionDiscountPayload;
};

const ShopifySubscriptionDiscountForm = ({
	handleSubmit,
	loading,
	shopifySubscriptionDiscountOption,
	initialValues = values,
}: PropTypes) => {
	const formFields: IField[] = [
		{
			name: 'email',
			label: 'Email',
			type: 'email',
			placeholder: 'customer@example.com',
		},
		{
			name: 'planName',
			label: 'Plan Name',
			type: 'select',
			placeholder: 'Select a plan',
			options: [
				{ value: '', label: 'Select a plan' },
				{ value: SubscriptionPlans.sapphire, label: 'Sapphire' },
				{ value: SubscriptionPlans.ruby, label: 'Ruby' },
				{ value: SubscriptionPlans.diamond, label: 'Diamond' },
			],
		},
		{
			name: 'percentage',
			label: 'Discount Percentage (%)',
			type: 'number',
			placeholder: '20 (0-100%)',
		},
		{
			name: 'durationMonths',
			label: 'Duration (Months)',
			type: 'number',
			placeholder: '1 (1-12 months)',
		},
	];

	// Convert form percentage (0-100) to API percentage (0-1)
	const convertFormToApiPercentage = (formPercentage: number): number => {
		return formPercentage / 100;
	};

	const handleInitialValues = (
		initialValues: ICreateShopifySubscriptionDiscountPayload,
	): ICreateShopifySubscriptionDiscountPayload => {
		return {
			email: initialValues.email,
			planName: initialValues.planName,
			percentage: initialValues.percentage,
			durationMonths: initialValues.durationMonths,
		};
	};

	const handleFormFields = (formFields: IField[]) => {
		if (
			shopifySubscriptionDiscountOption ===
			ShopifySubscriptionDiscountOptions.VIEW
		) {
			formFields.forEach((field) => (field.disabled = true));
			return formFields;
		}
		return formFields;
	};

	const handleSubmitType = (
		values: ICreateShopifySubscriptionDiscountPayload,
	) => {
		if (
			shopifySubscriptionDiscountOption ===
			ShopifySubscriptionDiscountOptions.VIEW
		)
			return;

		// Convert form percentage (0-100) to API percentage (0-1)
		const apiValues = {
			...values,
			percentage: convertFormToApiPercentage(values.percentage),
		};

		return handleSubmit(apiValues);
	};

	return (
		<div className="flex flex-col w-full">
			<h3
				className="font-bold text-lg uppercase text-center mt-4"
				data-test="shopify-subscription-discount-title"
			>
				{shopifySubscriptionDiscountOption ===
				ShopifySubscriptionDiscountOptions.UPDATE
					? 'Update Shopify Subscription Discount'
					: shopifySubscriptionDiscountOption ===
					  ShopifySubscriptionDiscountOptions.CREATE
					? 'Create new Shopify Subscription Discount'
					: 'View Shopify Subscription Discount'}
			</h3>
			<Formik
				initialValues={handleInitialValues(initialValues)}
				onSubmit={(values) => handleSubmitType(values)}
				validationSchema={shopifySubscriptionDiscountSchema}
			>
				<Form className="w-full">
					{handleFormFields(formFields).map((field) => (
						<FormField key={field.name} {...field} />
					))}

					{shopifySubscriptionDiscountOption !==
						ShopifySubscriptionDiscountOptions.VIEW && (
						<Button
							type="submit"
							className="w-full my-2"
							shape="rounded"
							isLoading={loading}
							disabled={loading}
							data-test="submit-shopify-subscription-discount-button"
						>
							Submit
						</Button>
					)}
				</Form>
			</Formik>
		</div>
	);
};

export default ShopifySubscriptionDiscountForm;
